"use client";

import { resetPassword } from "@/app/(auth)/reset-password/actions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AuthSuccess, AuthSuccessMessages } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconEye, IconEyeOff, IconLock } from "@tabler/icons-react";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const resetPasswordSchema = z.object({
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" }),
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

type PasswordResetModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PasswordResetModal({
  open,
  onOpenChange,
}: PasswordResetModalProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
    },
  });

  const onClose = (isOpen: boolean) => {
    if (!isOpen) {
      form.reset();
    }
    onOpenChange(isOpen);
  };

  const onSubmit = (data: ResetPasswordFormData) => {
    startTransition(async () => {
      const formData = new FormData();
      formData.append("password", data.password);
      const result = await resetPassword(formData);

      if (result.success) {
        toast.success(AuthSuccessMessages[AuthSuccess.PASSWORD_UPDATED]);

        router.refresh();
        onClose(false);
      } else {
        toast.error(result.error);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full gap-0 px-5 pt-14 pb-6 sm:max-w-sm sm:px-10 sm:py-14">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-semibold">
            Set new password
          </DialogTitle>
          <DialogDescription className="mt-2 text-center">
            Enter your new password below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-6 space-y-4"
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <IconLock className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                      <Input
                        {...field}
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="New Password"
                        disabled={isPending}
                        className="border-zinc-200 bg-zinc-50 pr-10 pl-10 dark:border-zinc-700 dark:bg-zinc-800"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute top-1/2 right-3 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                      >
                        {showPassword ? (
                          <IconEyeOff className="h-4 w-4" />
                        ) : (
                          <IconEye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className={cn(
                "h-11 w-full font-medium text-white",
                "bg-linear-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
              disabled={isPending}
              loading={isPending}
            >
              {isPending ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
